import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../App.jsx';
import api from '../services/api.js';
import { APP_VERSION } from '../version.js';
import NavBar from '../components/NavBar.jsx';

const THEME_ICONS = {
  espace: '🚀', geographie: '🌍', mythologie: '⚡', 'corps-humain': '🫀',
  histoire: '⏳', animaux: '🦁', personnages: '👤',
  sport: '⚽', inventions: '💡', saveurs: '🌶️',
};

export default function Connections() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedConn, setSelectedConn] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.getConnections().then(d => setData(d)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const openConnection = async (conn) => {
    setSelectedConn(conn);
    if (!conn.is_read) {
      try {
        await api.markConnectionRead(conn.id);
        setData(prev => ({
          ...prev,
          unread_count: Math.max(0, (prev.unread_count || 0) - 1),
          connections: prev.connections.map(c => c.id === conn.id ? { ...c, is_read: true } : c),
        }));
      } catch (e) { console.error(e); }
    }
  };

  if (loading) return <div className="loading-screen"><div className="loading-rocket">🔗</div><p>Recherche de connexions...</p></div>;

  const connections = data?.connections || [];
  const unreadCount = data?.unread_count || 0;
  const filtered = filter === 'unread' ? connections.filter(c => !c.is_read) : connections;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <NavBar />
      <div className="container">
        <button className="back-btn" onClick={() => navigate('/')}>← Retour</button>
        <div className="connections-header">
          <h2 className="connections-title">🔗 Connexions</h2>
          <p className="connections-subtitle">Clique sur une connexion pour la lire et tester tes connaissances !</p>
          {connections.length > 0 && (
            <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'center' }}>
              <button className={`btn ${filter === 'all' ? 'btn-primary' : ''}`}
                style={{ padding: '8px 18px', fontSize: '0.85rem', minWidth: 0 }}
                onClick={() => setFilter('all')}>Toutes ({connections.length})</button>
              <button className={`btn ${filter === 'unread' ? 'btn-primary' : ''}`}
                style={{ padding: '8px 18px', fontSize: '0.85rem', minWidth: 0, position: 'relative' }}
                onClick={() => setFilter('unread')}>
                Non lues
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -6, right: -6, background: '#ff4444', color: '#fff',
                    borderRadius: '50%', width: 20, height: 20, fontSize: '0.7rem', fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{unreadCount}</span>
                )}
              </button>
            </div>
          )}
        </div>

        {connections.length === 0 ? (
          <div className="cards-empty">
            <div className="emoji">🌉</div>
            <h3>Pas encore de connexions</h3>
            <p style={{ marginTop: 8, color: 'var(--text-muted)', maxWidth: 400, margin: '8px auto 0' }}>
              Joue des quiz dans différents thèmes pour découvrir les connexions cachées !</p>
            <button className="btn btn-primary" style={{ maxWidth: 300, margin: '24px auto 0' }}
              onClick={() => navigate('/')}>🚀 Explorer les thèmes</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="cards-empty">
            <div className="emoji">✅</div>
            <h3>Tout lu !</h3>
            <p style={{ marginTop: 8, color: 'var(--text-muted)' }}>Tu as lu toutes tes connexions.</p>
          </div>
        ) : (
          <div className="connections-grid">
            {filtered.map((conn, i) => (
              <motion.div key={conn.id || i} className="connection-card"
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                style={{ position: 'relative', borderColor: !conn.is_read ? 'rgba(52,224,196,0.4)' : undefined }}
                onClick={() => openConnection(conn)}>
                {!conn.is_read && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                    <span style={{ background: '#34e0c4', color: '#000', borderRadius: 8,
                      padding: '2px 8px', fontSize: '0.65rem', fontWeight: 800 }}>NOUVEAU</span>
                  </div>
                )}
                <div className="connection-bridge">
                  <div className="connection-theme" style={{ borderColor: conn.card_a.theme_color }}>
                    <span className="connection-theme-icon">{conn.card_a.theme_icon || THEME_ICONS[conn.card_a.theme_slug] || '📖'}</span>
                    <span className="connection-theme-name">{conn.card_a.theme_name.split(' ')[0]}</span>
                  </div>
                  <div className="connection-link">
                    <div className="connection-line" /><span className="connection-tag">{conn.emoji}</span><div className="connection-line" />
                  </div>
                  <div className="connection-theme" style={{ borderColor: conn.card_b.theme_color }}>
                    <span className="connection-theme-icon">{conn.card_b.theme_icon || THEME_ICONS[conn.card_b.theme_slug] || '📖'}</span>
                    <span className="connection-theme-name">{conn.card_b.theme_name.split(' ')[0]}</span>
                  </div>
                </div>
                <div className="connection-cards-preview">
                  <div className="connection-card-mini" style={{ borderLeftColor: conn.card_a.theme_color }}>💡 {conn.card_a.title}</div>
                  <div className="connection-card-mini" style={{ borderLeftColor: conn.card_b.theme_color }}>💡 {conn.card_b.title}</div>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.4 }}>
                  {conn.explanation.length > 80 ? conn.explanation.substring(0, 80) + '...' : conn.explanation}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedConn && (
          <motion.div className="modal-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSelectedConn(null)}>
            <motion.div className="modal connection-modal"
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}>
              <div className="connection-modal-header"
                style={{ position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 2,
                  display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.5rem' }}>{selectedConn.emoji}</span>
                <h3 style={{ margin: 0 }}>Connexion découverte !</h3>
                <button onClick={() => setSelectedConn(null)} aria-label="Fermer"
                  style={{ marginLeft: 'auto', background: 'transparent', border: 'none',
                    color: 'var(--text)', fontSize: '1.6rem', lineHeight: 1, cursor: 'pointer', padding: '0 4px' }}>✕</button>
              </div>
              <div className="connection-modal-explanation">{selectedConn.explanation}</div>
              <div className="connection-modal-cards">
                <div className="connection-modal-card" style={{ borderColor: selectedConn.card_a.theme_color }}>
                  <div className="connection-modal-card-theme">{selectedConn.card_a.theme_icon || THEME_ICONS[selectedConn.card_a.theme_slug] || '📖'} {selectedConn.card_a.theme_name}</div>
                  <h4>💡 {selectedConn.card_a.title}</h4>
                  <p>{selectedConn.card_a.content}</p>
                </div>
                <div className="connection-modal-bridge">
                  <div className="connection-modal-bridge-line" />
                  <span className="connection-modal-bridge-tag">{selectedConn.emoji}</span>
                  <div className="connection-modal-bridge-line" />
                </div>
                <div className="connection-modal-card" style={{ borderColor: selectedConn.card_b.theme_color }}>
                  <div className="connection-modal-card-theme">{selectedConn.card_b.theme_icon || THEME_ICONS[selectedConn.card_b.theme_slug] || '📖'} {selectedConn.card_b.theme_name}</div>
                  <h4>💡 {selectedConn.card_b.title}</h4>
                  <p>{selectedConn.card_b.content}</p>
                </div>
              </div>

              <button className="btn btn-primary modal-close" onClick={() => setSelectedConn(null)}
                style={{ marginTop: 16 }}>Fermer</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
